import type { Epic } from '../../index.ts'
import type { Action } from '@reduxjs/toolkit'
import { isAnyOf } from '@reduxjs/toolkit'
import { cubeStateKey, visualizationSlice } from '../../Slice/Visualization/VisualizationSlice.ts'
import { filter, mergeMap, of, withLatestFrom } from 'rxjs'

/**
 * Translate workspace-level actions into `cubeRequested` dispatches.
 *
 * The cube depends only on `(scopeId, blastType)` — every other knob (selectedPath,
 * gradeThreshold, scalingMode, countMode, bucket assignment) is FE-only. So this epic
 * narrows the broader workspace lifecycle down to just those two and emits a `cubeRequested`
 * only for `(scopeId, blastType)` pairs that aren't already tracked in `state.visualization.cubes`.
 *
 * Triggers:
 *   - `setScope`           workspace switches to a different project/order
 *   - `bucketsReady`       a source's sample list is known — emits one request per source
 *   - `updateSourceShared` a source's `blastType` may have changed
 *
 * ## Dedup is state-derived
 *
 * Whether to emit `cubeRequested` is decided from `state.visualization.cubes`, not a module-local
 * map. An already-tracked (`loading`/`ready`) cube is skipped, so an unrelated `updateSourceShared`
 * neither re-fetches nor flips a `ready` cube back to `loading`; an `error` entry stays
 * re-requestable so a later knob change can retry a failed load. Because the slice is wiped by
 * `reset()` — the loader's `useEffect` dispatches it on workspace (re-)entry — the next trigger
 * correctly re-requests every cube.
 */
export const cubeAutoRequestEpic: Epic = (action$, state$, { [visualizationSlice.name]: visualization }) => {
  const triggers = isAnyOf(
    visualization.setScope,
    visualization.bucketsReady,
    visualization.updateSourceShared
  )

  return action$.pipe(
    filter((a: Action) => triggers(a)),
    withLatestFrom(state$),
    mergeMap(([, s]) => {
      const scopeId = s.visualization.scope?.id
      if (!scopeId) return of()

      const requests: Action[] = []
      const seen = new Set<string>()
      for (const src of Object.values(s.visualization.sources)) {
        const blastType = src?.shared?.blastType
        if (!blastType) continue
        // A source with `spin` set is sourced from the SPIN cube (algorithm + point-in-time);
        // it participates in the cube identity so it gets its own key/entry/fetch.
        const spin = src?.shared?.spin
        const key = cubeStateKey(scopeId, blastType, spin)
        if (seen.has(key)) continue // two sources, same (scope, blastType, spin) → request once
        seen.add(key)

        const entry = s.visualization.cubes[key]
        // Request only when untracked or previously errored — never re-issue for a cube that is
        // already loading or ready (that would flip a ready cube back to the loading spinner).
        if (entry && entry.status !== 'error') continue

        requests.push(visualization.cubeRequested({ scopeId, blastType, spin }))
      }
      return of(...requests)
    })
  )
}
